// Safe external-link opener for iOS Safari COOP blocks + iframe-embed scenarios.
//
// Background: iOS Safari 17/18 blocks `<a target="_blank">` to cross-origin
// URLs with: "Navigation was blocked by Cross-Origin-Opener-Policy".
// Additionally, when our app is embedded inside an iframe (e.g. the Emergent
// dashboard's "App Preview" panel, or any third-party embed), even `href=`
// without a target fails because the iframe tries to navigate ITSELF to
// YouTube — and YouTube's COOP refuses to load inside a cross-origin frame.
//
// The robust fix: use `target="_top"` on iOS or when embedded. `_top`:
//   • In iframe → breaks out of the iframe and navigates the top window
//     (Safari's main tab) → YouTube loads in the parent tab, COOP satisfied
//   • Not in iframe → behaves identically to no-target (same-tab nav)
// Combined with Universal Links, iOS still deep-links into the YouTube app
// when installed.
//
// Desktop (Mac/Windows Chrome/Firefox) gets `target="_blank"` for new-tab UX
// EXCEPT when embedded — then we use `_top` to escape the embed.
//
// Usage:
//   <a {...externalLinkProps(url)} onClick={...}>...</a>

function isIosLike() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);
}

function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  return (
    window.navigator?.standalone === true ||
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches)
  );
}

function isEmbedded() {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch (_) {
    // Cross-origin access to window.top throws — that's a definitive YES
    return true;
  }
}

/**
 * Returns `{ href, target, rel }` for cross-origin external links:
 *   - Embedded (iframe) → target="_top" → breaks out of the iframe
 *   - iOS / PWA-standalone → target="_top" → safe everywhere on iOS
 *   - Android / desktop → target="_blank" → standard new-tab
 */
export function externalLinkProps(url) {
  if (!url) return {};
  if (isEmbedded() || isIosLike() || isStandalonePwa()) {
    // `_top` breaks out of any iframe & is harmless on a normal page.
    return { href: url, target: "_top", rel: "noopener noreferrer" };
  }
  return { href: url, target: "_blank", rel: "noopener noreferrer" };
}

/**
 * Optional onClick handler — kept for backwards compatibility.
 */
export function openExternal(url) {
  return (e) => {
    if (!url) return;
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) return;
    if (isEmbedded() || isIosLike() || isStandalonePwa()) {
      e?.preventDefault?.();
      try {
        if (window.top) {
          window.top.location.assign(url);
          return;
        }
      } catch (_) {
        // Cross-origin window.top → fall through to current-window nav
      }
      window.location.assign(url);
    }
  };
}
